'use client';

import { Box } from '@ironhack/design-system2/components/box';
import { forwardRef } from '@ironhack/design-system2/components/forward-ref';
import {
  Link as DsLink,
  type LinkProps,
} from '@ironhack/design-system2/components/link';
import { type As, type ComponentWithAs } from '@ironhack/design-system2/types';
import NextLink from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { omit } from 'ramda';
import {
  type MouseEvent,
  type PropsWithChildren,
  type ReactElement,
} from 'react';

import { type BasePageParams } from '@/app/urls/url-mapping';
import config from '@/config';
import featureToggles from '@/config/feature-toggles';
import { usePageContext } from '@/contexts/page-context/use-page-context';
import { type DatoRoute, type DatoTargetBlock } from '@/lib/datocms/types';
import { sendEvent } from '@/lib/gtm';
import { type WithTracking } from '@/types';
import {
  getArticleHref,
  getScrollBehavior,
  replacePlaceholderLink,
  updateUrlRegion,
} from './link-utils';

type Props = WithTracking<DatoTargetBlock> &
  LinkProps &
  PropsWithChildren<{
    withoutDecoration?: boolean;
  }>;

const getPageHref = (route: DatoRoute): string =>
  featureToggles['dato-route-updated-paths']
    ? route.updatedPath || route.path
    : route.path;

type GetHrefOptions = Pick<BasePageParams, 'language' | 'region'> & {
  withOrigin?: boolean;
};

export const getHref = (
  target: DatoTargetBlock | null | undefined,
  options: GetHrefOptions
): string => {
  const { withOrigin, language, region } = { withOrigin: false, ...options };

  let href = withOrigin ? '' : '#/';
  if (!target) return href;

  const { anchor, blogArticle, link, route } = target;
  if (anchor) return `#${anchor}`;
  if (link) {
    href = replacePlaceholderLink(link, { language, region });
  }
  if (blogArticle?.previewUrl) href = getArticleHref(blogArticle.previewUrl);
  if (route) href = getPageHref(route);

  const origin = withOrigin && href.startsWith('/') ? config.baseUrl : '';
  return updateUrlRegion(`${origin}${href}`);
};

// Needs forwardRef so IronhackLogoLink can pass ref for PopoverTrigger
export const Link: ComponentWithAs<As, Props> = forwardRef(
  (props: Props, ref): ReactElement => {
    const {
      anchor,
      children,
      gaEvent: [gaEvent] = [],
      io: isObfuscated = false,
      onClick,
      tracking,
      withoutDecoration,
      ...linkProps
    } = omit(['article', 'blogArticle', 'link', 'route'], props);

    const {
      pageData: { gaPageType },
      params: { language, region },
    } = usePageContext();
    const router = useRouter();
    const pathname = usePathname() ?? '';

    const href = getHref(props, { language, region });
    const isExternal = !anchor && href.startsWith('http');
    const variant = withoutDecoration ? 'withoutDecoration' : 'default';

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
      if (tracking?.f) sessionStorage.setItem('formReferrer', tracking?.f);

      if (anchor || isObfuscated) event.preventDefault();

      if (anchor) {
        const targetElement = document.querySelector(`#${CSS.escape(anchor)}`);
        if (!targetElement) return;
        targetElement.scrollIntoView({
          behavior: getScrollBehavior(),
          block: 'start',
        });
        router.replace(`${pathname}#${anchor}`, { scroll: false });
      } else if (isObfuscated) {
        if (isExternal) {
          window.open(href);
        } else {
          router.push(href);
        }
      }

      if (gaEvent) sendEvent({ ...gaEvent, pageType: gaPageType });
      if (onClick) onClick(event);
    };

    const BaseLink = (baseLinkProps: LinkProps) => (
      <DsLink
        onClick={handleClick}
        ref={ref}
        variant={variant}
        {...baseLinkProps}
        {...linkProps}
      />
    );

    if (isObfuscated) return <BaseLink as={Box}>{children}</BaseLink>;
    if (anchor) return <BaseLink href={href}>{children}</BaseLink>;
    if (isExternal)
      return (
        <BaseLink display="flex" href={href} isExternal>
          {children}
        </BaseLink>
      );
    return (
      <BaseLink as={NextLink} href={href}>
        {children}
      </BaseLink>
    );
  }
);
